/**
 * Languages offered when adding one to a form.
 *
 * A convenience, not a constraint. The server validates the *shape* of a language code, not
 * its membership of any list, and deliberately so: somebody translating a form into Welsh,
 * Basque or a regional variant nobody anticipated should not have to wait for a release.
 * So this list covers what people reach for, and "Other…" stays available for the rest.
 *
 * Native names are listed first because they are what a translator recognises — somebody
 * assigned the French translation is looking for "Français", not for the English word
 * "French" halfway down an alphabetical list.
 */
export interface LanguageOption {
  code: string;
  /** The language's own name for itself. */
  native: string;
  /** The English name, for an author who does not read the script. */
  english: string;
  rtl?: boolean;
}

export const LANGUAGES: readonly LanguageOption[] = [
  { code: 'ar', native: 'العربية', english: 'Arabic', rtl: true },
  { code: 'bn', native: 'বাংলা', english: 'Bengali' },
  { code: 'bg', native: 'Български', english: 'Bulgarian' },
  { code: 'ca', native: 'Català', english: 'Catalan' },
  { code: 'zh-Hans', native: '简体中文', english: 'Chinese (Simplified)' },
  { code: 'zh-Hant', native: '繁體中文', english: 'Chinese (Traditional)' },
  { code: 'hr', native: 'Hrvatski', english: 'Croatian' },
  { code: 'cs', native: 'Čeština', english: 'Czech' },
  { code: 'da', native: 'Dansk', english: 'Danish' },
  { code: 'nl', native: 'Nederlands', english: 'Dutch' },
  { code: 'en', native: 'English', english: 'English' },
  { code: 'en-GB', native: 'English (UK)', english: 'English (United Kingdom)' },
  { code: 'en-US', native: 'English (US)', english: 'English (United States)' },
  { code: 'et', native: 'Eesti', english: 'Estonian' },
  { code: 'fi', native: 'Suomi', english: 'Finnish' },
  { code: 'fr', native: 'Français', english: 'French' },
  { code: 'fr-CA', native: 'Français (Canada)', english: 'French (Canada)' },
  { code: 'de', native: 'Deutsch', english: 'German' },
  { code: 'el', native: 'Ελληνικά', english: 'Greek' },
  { code: 'gu', native: 'ગુજરાતી', english: 'Gujarati' },
  { code: 'he', native: 'עברית', english: 'Hebrew', rtl: true },
  { code: 'hi', native: 'हिन्दी', english: 'Hindi' },
  { code: 'hu', native: 'Magyar', english: 'Hungarian' },
  { code: 'id', native: 'Bahasa Indonesia', english: 'Indonesian' },
  { code: 'it', native: 'Italiano', english: 'Italian' },
  { code: 'ja', native: '日本語', english: 'Japanese' },
  { code: 'kn', native: 'ಕನ್ನಡ', english: 'Kannada' },
  { code: 'ko', native: '한국어', english: 'Korean' },
  { code: 'lv', native: 'Latviešu', english: 'Latvian' },
  { code: 'lt', native: 'Lietuvių', english: 'Lithuanian' },
  { code: 'ms', native: 'Bahasa Melayu', english: 'Malay' },
  { code: 'ml', native: 'മലയാളം', english: 'Malayalam' },
  { code: 'mr', native: 'मराठी', english: 'Marathi' },
  { code: 'nb', native: 'Norsk bokmål', english: 'Norwegian' },
  { code: 'fa', native: 'فارسی', english: 'Persian', rtl: true },
  { code: 'pl', native: 'Polski', english: 'Polish' },
  { code: 'pt', native: 'Português', english: 'Portuguese' },
  { code: 'pt-BR', native: 'Português (Brasil)', english: 'Portuguese (Brazil)' },
  { code: 'pa', native: 'ਪੰਜਾਬੀ', english: 'Punjabi' },
  { code: 'ro', native: 'Română', english: 'Romanian' },
  { code: 'ru', native: 'Русский', english: 'Russian' },
  { code: 'sr', native: 'Српски', english: 'Serbian' },
  { code: 'sk', native: 'Slovenčina', english: 'Slovak' },
  { code: 'sl', native: 'Slovenščina', english: 'Slovenian' },
  { code: 'es', native: 'Español', english: 'Spanish' },
  { code: 'es-419', native: 'Español (Latinoamérica)', english: 'Spanish (Latin America)' },
  { code: 'sw', native: 'Kiswahili', english: 'Swahili' },
  { code: 'sv', native: 'Svenska', english: 'Swedish' },
  { code: 'ta', native: 'தமிழ்', english: 'Tamil' },
  { code: 'te', native: 'తెలుగు', english: 'Telugu' },
  { code: 'th', native: 'ไทย', english: 'Thai' },
  { code: 'tr', native: 'Türkçe', english: 'Turkish' },
  { code: 'uk', native: 'Українська', english: 'Ukrainian' },
  { code: 'ur', native: 'اردو', english: 'Urdu', rtl: true },
  { code: 'vi', native: 'Tiếng Việt', english: 'Vietnamese' },
];

const BY_CODE = new Map(LANGUAGES.map((language) => [language.code, language]));

/**
 * A language's name for display.
 *
 * Falls back to the code itself for anything added through "Other…", so a form using a
 * language this list does not know still reads sensibly rather than showing a blank.
 */
export function languageName(code: string): string {
  const known = BY_CODE.get(code);
  if (known) return known.native;

  // A regional variant of a language we know — "fr-BE" — is still recognisably French.
  const base = BY_CODE.get(code.split('-')[0] ?? '');
  return base ? `${base.native} (${code})` : code;
}

export function isRtlLanguage(code: string): boolean {
  const base = code.split('-')[0] ?? '';
  return BY_CODE.get(code)?.rtl ?? BY_CODE.get(base)?.rtl ?? false;
}
